import _ from '@lodash';
import axios from 'axios';
import moment from 'moment-timezone';
import { createEntityAdapter, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiEndpoints from 'app/constants/apiEndpoints';
import { dateToString } from 'app/main/utils/dateUtils';
import { userLoggedOut } from 'app/auth/store/userSlice';

const { CLOSING_EVENTS, CLOSING_EVENTS_FOLDERS, CLOSING_EVENTS_OFFICER } = apiEndpoints;

export const mapApiToUiEvents = data =>
  data.map(event => ({
    ...event,
    collectedFees: {
      ...event.collectedFees
    }
  }));

export const getEvents = createAsyncThunk('closingEvents/events/getEvents', async params => {
  const axiosParams = params && params.query ? { params: { ...params.query } } : null;
  console.log('[MILESTONE] CLOSING_EVENTS params =>', axiosParams);
  const response = await axios.get(CLOSING_EVENTS, axiosParams);
  const data = await response.data.data.closingEvents;
  // const prueba = mapApiToUiEvents(data);
  return mapApiToUiEvents(data);
});

export const getEventFolders = createAsyncThunk('closingEvents/eventFolders/getEventFolders', async params => {
  const axiosParams = params && params.query ? { params: { ...params.query } } : null;
  const response = await axios.get(`${CLOSING_EVENTS_FOLDERS}`, axiosParams);
  const data = await response.data;
  return data.data;
});

export const getEventOfficer = createAsyncThunk('closingEvents/eventOfficer/getEventOfficer', async params => {
  const axiosParams = params && params.query ? { params: { ...params.query } } : null;
  const response = await axios.get(`${CLOSING_EVENTS_OFFICER}`, axiosParams);
  const data = await response.data;
  return data.data;
});

export const getBestMonthEvents = createAsyncThunk('closingEvents/bestMonthEvents/getBestMonthEvents', async params => {
  const axiosParams = params && params.query ? { params: { ...params.query } } : null;
  const response = await axios.get(`${CLOSING_EVENTS}best-month`, axiosParams);
  const data = await response.data.data.closingEvents;
  const results = mapApiToUiEvents(data);
  return {
    bestBy: params.query.bestBy,
    results
  };
});

export const addEvent = createAsyncThunk('closingEvents/events/addEvent', async closingEvent => {
  const response = await axios.post(CLOSING_EVENTS, { closingEvent });
  const data = await response.data;
  return data.data.closingEvents;
});

export const updateEvent = createAsyncThunk('closingEvents/events/updateEvent', async closingEvent => {
  const response = await axios.put(CLOSING_EVENTS + closingEvent.id, { closingEvent });
  const data = await response.data;
  return data.data.closingEvent;
});

const eventsAdapter = createEntityAdapter({});

export const {
  selectAll: selectEvents,
  selectIds: selectEventIds,
  selectById: selectEventById
} = eventsAdapter.getSelectors(state => state.closingEvents.events);

export const selectBestMonth = state => state.closingEvents.events.bestMonth;

export const responseStatus = state => state.closingEvents.events.bestMonthstatus;

const initialState = eventsAdapter.getInitialState({
  eventDialog: {
    type: 'new',
    props: {
      open: false
    },
    data: null
  },
  eventFolders: [],
  eventOfficer: []
});

const eventsSlice = createSlice({
  name: 'closingEvents/events',
  initialState,
  reducers: {
    pickCalendarDate: (state, action) => {
      return {
        ...state,
        pickedCalendarDate: action.payload
      };
    },
    openNewEventDialog: {
      prepare: event => {
        const payload = {
          type: 'new',
          props: {
            open: true
          },
          data: {
            loan: {
              closingDate: dateToString(
                moment({
                  year: event.start.year(),
                  month: event.start.month(),
                  day: event.start.date()
                })
              )
            }
          }
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.eventDialog = action.payload;
      }
    },
    openEditEventDialog: {
      prepare: event => {
        const payload = {
          type: 'edit',
          props: {
            open: true
          },
          data: {
            ...event,
            loan: {
              ...event.loan,
              closingDate: dateToString(event.loan.closingDate)
            }
          }
        };
        return { payload };
      },
      reducer: (state, action) => {
        state.eventDialog = action.payload;
      }
    },
    // eslint-disable-next-line no-unused-vars
    closeNewEventDialog: (state, action) => {
      state.eventDialog = {
        type: 'new',
        props: {
          open: false
        },
        data: null
      };
    },
    // eslint-disable-next-line no-unused-vars
    closeEditEventDialog: (state, action) => {
      state.eventDialog = {
        type: 'edit',
        props: {
          open: false
        },
        data: null
      };
    },
    clearStatus: state => {
      return {
        ...state,
        status: {}
      };
    },
    clearEvents: () => initialState
  },
  extraReducers: {
    [getEvents.fulfilled]: eventsAdapter.setAll,
    [addEvent.fulfilled]: eventsAdapter.addOne,
    [updateEvent.fulfilled]: eventsAdapter.upsertOne,
    [getBestMonthEvents.fulfilled]: (state, action) => {
      return {
        ...state,
        bestMonth: {
          ...state.bestMonth,
          [action.payload.bestBy]: action.payload.results
        }
      };
    },
    [getBestMonthEvents.rejected]: state => {
      return {
        ...state,
        status: {
          serverError: true
        }
      };
    },
    [getEventFolders.fulfilled]: (state, action) => {
      return {
        ...state,
        eventFolders: action.payload.folders
      };
    },
    [getEventOfficer.fulfilled]: (state, action) => {
      return {
        ...state,
        eventOfficer: action.payload.officer
      };
    },
    [getEventFolders.rejected]: state => {
      return {
        ...state,
        eventFolders: [],
        status: {
          serverError: true
        }
      };
    },
    [getEventOfficer.rejected]: state => {
      return {
        ...state,
        eventOfficer: [],
        status: {
          serverError: true
        }
      };
    },
    [userLoggedOut]: () => initialState
  }
});

export const selectPickedCalendarDate = state => _.get(state, 'closingEvents.events.pickedCalendarDate');

export const {
  pickCalendarDate,
  openNewEventDialog,
  closeNewEventDialog,
  openEditEventDialog,
  closeEditEventDialog,
  clearEvents,
  clearStatus
} = eventsSlice.actions;

export default eventsSlice.reducer;
